import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "../App";
import DashboardLayout from "../components/DashboardLayout";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Progress } from "../components/ui/progress";
import { useLanguage } from "../context/LanguageContext";
import { 
  BarChart3, TrendingUp, 
  Target, BookOpen, AlertCircle
} from "lucide-react";

const Analytics = ({ auth }) => {
  const { t, language } = useLanguage();
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchAnalytics();
  }, []);

  const fetchAnalytics = async () => {
    try {
      const response = await axios.get(`${API}/analytics`, {
        withCredentials: true,
        headers: auth.token ? { Authorization: `Bearer ${auth.token}` } : {}
      });
      setAnalytics(response.data);
    } catch (error) {
      console.error("Error fetching analytics:", error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <DashboardLayout auth={auth}>
        <div className="flex items-center justify-center h-64">
          <div className="w-12 h-12 border-4 border-maple border-t-transparent rounded-full animate-spin" />
        </div>
      </DashboardLayout>
    );
  }

  const progress = analytics?.progress || {};
  const chapterStats = analytics?.chapter_stats || {};
  const weakAreas = analytics?.weak_areas || [];

  return (
    <DashboardLayout auth={auth}>
      <div className="space-y-8" data-testid="analytics-page">
        {/* Header */}
        <div>
          <h1 className="font-heading text-3xl font-bold text-slate-900 mb-2">{t('nav.analytics')}</h1>
          <p className="text-slate-600">
            {language === 'fr' 
              ? 'Suivez vos progrès et identifiez les domaines à améliorer'
              : 'Track your progress and identify areas for improvement'
            }
          </p>
        </div>

        {/* Overview Stats */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <Card className="border-0 shadow-sm">
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
                  <Target className="w-6 h-6 text-blue-600" />
                </div>
                <div>
                  <p className="text-2xl font-heading font-bold text-slate-900">{progress.tests_taken || 0}</p>
                  <p className="text-sm text-slate-600">Tests Taken</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-0 shadow-sm">
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center">
                  <TrendingUp className="w-6 h-6 text-green-600" />
                </div>
                <div>
                  <p className="text-2xl font-heading font-bold text-slate-900">{progress.average_score || 0}%</p>
                  <p className="text-sm text-slate-600">Average Score</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-0 shadow-sm">
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-yellow-100 rounded-xl flex items-center justify-center">
                  <BarChart3 className="w-6 h-6 text-yellow-600" />
                </div>
                <div>
                  <p className="text-2xl font-heading font-bold text-slate-900">{progress.best_score || 0}%</p>
                  <p className="text-sm text-slate-600">Best Score</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-0 shadow-sm">
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-maple-50 rounded-xl flex items-center justify-center">
                  <Target className="w-6 h-6 text-maple" />
                </div>
                <div>
                  <p className="text-2xl font-heading font-bold text-slate-900">{progress.tests_passed || 0}</p>
                  <p className="text-sm text-slate-600">Tests Passed</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        <div className="grid lg:grid-cols-2 gap-6">
          {/* Chapter Performance */}
          <Card className="border-0 shadow-sm">
            <CardHeader>
              <CardTitle className="font-heading text-lg flex items-center gap-2">
                <BookOpen className="w-5 h-5 text-truenorth" />
                Chapter Performance
              </CardTitle>
            </CardHeader>
            <CardContent>
              {Object.keys(chapterStats).length > 0 ? (
                <div className="space-y-4">
                  {Object.entries(chapterStats).map(([chapter, stats]) => {
                    const accuracy = stats.total > 0 ? Math.round((stats.correct / stats.total) * 100) : 0;
                    return (
                      <div key={chapter}>
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-sm font-medium text-slate-700">{chapter}</span>
                          <span className={`text-sm font-medium ${accuracy >= 75 ? 'text-green-600' : accuracy >= 50 ? 'text-yellow-600' : 'text-red-600'}`}>
                            {accuracy}%
                          </span>
                        </div>
                        <Progress value={accuracy} className="h-2" />
                        <p className="text-xs text-slate-500 mt-1">
                          {stats.correct} of {stats.total} correct
                        </p>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="text-center py-8 text-slate-500">
                  <BookOpen className="w-12 h-12 mx-auto mb-3 opacity-50" />
                  <p>Take some practice tests to see your chapter performance</p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Weak Areas */}
          <Card className="border-0 shadow-sm">
            <CardHeader>
              <CardTitle className="font-heading text-lg flex items-center gap-2">
                <AlertCircle className="w-5 h-5 text-yellow-500" />
                Areas to Improve
              </CardTitle>
            </CardHeader>
            <CardContent>
              {weakAreas.length > 0 ? (
                <div className="space-y-3">
                  {weakAreas.map((area, index) => (
                    <div key={index} className="p-4 bg-yellow-50 rounded-xl border border-yellow-100">
                      <div className="flex items-center justify-between">
                        <span className="font-medium text-slate-900">{area.chapter}</span>
                        <span className="text-sm text-yellow-700 bg-yellow-100 px-2 py-1 rounded">
                          {area.accuracy}% accuracy
                        </span>
                      </div>
                      <p className="text-sm text-slate-600 mt-1">
                        Focus on this chapter to improve your overall score
                      </p>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8">
                  <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-3">
                    <TrendingUp className="w-8 h-8 text-green-600" />
                  </div>
                  <p className="text-slate-600">
                    {progress.tests_taken > 0 
                      ? "Great job! No weak areas identified."
                      : "Take some tests to identify areas for improvement."}
                  </p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default Analytics;
